<template>
  <div v-if="report" class="new-report-wrap">
    <el-form
      :model="report"
      class="report-card el-card box-card is-always-shadow "
      :rules="rules"
      ref="reportForm">
      <div class="header-item customer-input-wrap">
        <!-- Tenant selection for super admins -->
        <el-form-item v-if="isSuperAdmin && !report.id" prop="tenantId" style="margin-bottom: 15px;">
          <el-select
            v-model="selectedTenantId"
            filterable
            clearable
            style="width: 100%;"
            :placeholder="$t('report.select_tenant')"
            @change="onTenantChange">
            <el-option
              v-for="tenant in tenants"
              :key="tenant.id"
              :label="tenant.name"
              :value="tenant.id">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item prop="customers">
          <el-tag
            class="customer-tag"
            :key="index"
            v-for="(selectedCustomer, index) in report.customers"
            :closable="!selectedCustomer.default"
            :disable-transitions="true"
            @close="removeCustomer(selectedCustomer)"
          >{{ selectedCustomer.name }}
          </el-tag>
          <el-dropdown
            class="dropdown-new-customer"
            trigger="click"
            @command="handleCustomerDropdownClick">
            <el-button type="text" icon="el-icon-circle-plus-outline" circle></el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="(customer, index) in allCustomers"
                :command="customer"
                :key="index"
              >{{ customer.name }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-form-item>
      </div>

      <!-- Warning Option -->
      <div class="header-item report-type-wrap">
        <el-form-item prop="warningOption">
          <el-select
            v-model="report.warningOption"
            @change="onWarningOptionChange"
            :placeholder="$t('new_report.select_type')"
            class="new-report-select-type"
          >
            <el-option
              v-for="item in categories"
              :key="item.value"
              :label="item.label"
              :value="item"
            ></el-option>
          </el-select>
        </el-form-item>
      </div>

      <!-- maintenance time range -->
      <div v-if="isMaintenanceReport" class="header-item report-type-wrap">
        <el-form-item prop="maintenanceTimeRange">
          <!-- normal date time range picker -->
          <template v-if="maintenanceCreation">
            <el-date-picker
              v-model="report.maintenanceTimeRange"
              type="datetimerange"
              :range-separator="$t('base.to')"
              :start-placeholder="$t('new_report.start_point_of_time')"
              :end-placeholder="$t('new_report.end_point_of_time')"
              format="dd.MM.yyyy HH:mm"
              :picker-options="pickerOptions"
            ></el-date-picker>
          </template>
          <!-- two separated date picker to extend the end date-->
          <template v-else>
            <i class="el-icon-time date-range-icon"></i>
            <el-tooltip effect="dark" :content="$t('current_reports.report_extend_time_range_readonly_hint')"
                        placement="top-start">
              <el-date-picker
                v-model="report.maintenanceTimeRange[0]"
                class="readonly-datepicker"
                type="datetime"
                readonly
                :range-separator="$t('base.to')"
                :start-placeholder="$t('new_report.start_point_of_time')"
                :end-placeholder="$t('new_report.end_point_of_time')"
                format="dd.MM.yyyy HH:mm"
                :picker-options="pickerOptions"
                prefix-icon="off"
              ></el-date-picker>
            </el-tooltip>
            <span class="datepicker-separator">{{ $t('base.to') }}</span>
            <el-date-picker
              v-model="maintenanceExtendedEndDate"
              type="datetime"
              @change="onMaintenanceExtendedEndDateChange()"
              :range-separator="$t('base.to')"
              :start-placeholder="$t('new_report.start_point_of_time')"
              :end-placeholder="$t('new_report.end_point_of_time')"
              format="dd.MM.yyyy HH:mm"
              :picker-options="pickerOptions"
              prefix-icon="off"
            ></el-date-picker>
          </template>
        </el-form-item>
      </div>

      <!-- Subject -->
      <div class="header-item report-subject">
        <el-form-item prop="subject">
          <el-input
            class="subject-input"
            :placeholder="$t('new_report.subject')"
            v-model="report.subject"
          ></el-input>
        </el-form-item>
      </div>

      <!-- Content -->
      <div v-if="!report.id" class="content">
        <el-form-item prop="content">
          <el-input class="report-body-input" type="textarea" :rows="8" v-model="report.content"></el-input>
        </el-form-item>
      </div>
      <div class="bottom" v-bind:class="{'edition-mobil':isMobil}">
        <el-button
          v-if="!report.id"
          type="success"
          v-bind:loading="saveInProgress"
          v-on:click="saveReport"
          icon="el-icon-check"
        >{{ $t('new_report.send_report') }}
        </el-button>
        <el-button
          v-if="report.id"
          type="success"
          v-bind:loading="saveInProgress"
          v-on:click="patchReport"
          icon="el-icon-check"
        >{{ $t('new_report.send_report') }}
        </el-button>
        <el-button
          type="default"
          v-on:click="cancelClick"
          icon="el-icon-close"
        >{{ $t('base.cancel') }}
        </el-button>
        <!-- display creator -->
        <div v-if="report.createdByName" class="edition-information-style">
          <b>{{ $t('current_reports.report_creator') }}: </b>
          <span>{{ report.createdByName }} </span>
          <span v-if="report.createdByAccount == null">({{ $t('settings.account.inactive') }})</span>
        </div>

      </div>
    </el-form>
    <edit-report-updates :report="report" @report-content-update="patchReport()"></edit-report-updates>
    <!-- delete -->
    <el-dialog
      :title="$t('new_report.delete_report')"
      :visible.sync="deleteReportDialog"
      width="30%"
    >
      <span>{{ $t('new_report.delete_dialog_text') }}</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deleteReportDialog = false">{{ $t('base.cancel') }}</el-button>
        <el-button type="danger" @click="deleteReport">{{ $t('new_report.delete_report') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import {WarningOptionLevelEnum} from "@/enum/WarningOptionLevelEnum";
import {WarningOptionTypeEnum} from "@/enum/WarningOptionTypeEnum";
import Report from "../../model/Report";
import EditReportUpdates from "../report/EditReportUpdates";
import moment from "moment";

const authStateMappers = createNamespacedHelpers("auth");
const mspStateMappers = createNamespacedHelpers("msp");
const applicationStateMappers = createNamespacedHelpers('application')

export default {
  name: "EditReport",
  components: {EditReportUpdates},
  data() {
    let checkCustomers = (rule, value, callback) => {
      if (value.length < 1) {
        return callback(new Error("Please input the age"));
      }
      callback();
    };
    let checkCategory = (rule, value, callback) => {
      if (!value) {
        return callback(new Error("Please input the age"));
      }
      callback();
    };
    let checkTimeRange = (rule, value, callback) => {
      if (!value || value.length != 2) {
        return callback(
          new Error("error in time range validation (empty values)")
        );
      }
      callback();
    };
    let checkTimeDuration = (rule, value, callback) => {
      const duration = Math.abs(value[0].valueOf() - value[1].valueOf());
      if (duration < 15 * 60 * 1000) {
        // 15 min
        return callback(
          new Error("error in time range validation (to small duration)")
        );
      }
      callback();
    };
    return {
      reportDoneDialogVisible: false,
      report: undefined,
      maintenanceExtendedEndDate: undefined,
      maintenanceCreation: true,
      maintenanceTimeRangeWasExtended: false,
      warningOptionCache: undefined,
      deleteReportDialog: false,
      saveInProgress: false,
      selectedTenantId: null,
      tenants: [],
      tenantCustomers: [],
      loadingTenants: false,
      loadingCustomers: false,
      pickerOptions: {
        firstDayOfWeek: 1,
        disabledDate: pickerDate => {
          const momentPickerDate = moment(pickerDate).startOf("day");
          const momentCompareDate = moment().startOf("day");
          return momentPickerDate.isBefore(momentCompareDate);
        }
      },
      rules: {
        subject: [
          {
            required: true,
            message: this.$t("new_report.subject_required"),
            trigger: "blur"
          }
        ],
        content: [
          {
            required: true,
            message: this.$t("new_report.content_required"),
            trigger: "blur"
          }
        ],
        warningOption: [
          {
            required: true,
            validator: checkCategory,
            message: this.$t("new_report.category_required"),
            trigger: "blur"
          }
        ],
        customers: [
          {
            required: true,
            validator: checkCustomers,
            message: this.$t("new_report.customer_required"),
            trigger: "blur"
          }
        ],
        tenantId: [
          {
            required: this.isSuperAdmin,
            message: this.$t("report.tenant_required"),
            trigger: "change"
          }
        ],
        maintenanceTimeRange: [
          {
            required: this.isMaintenanceReport,
            validator: checkTimeRange,
            message: this.$t("new_report.time_range_required"),
            trigger: "blur"
          },
          {
            required: this.isMaintenanceReport,
            validator: checkTimeDuration,
            message: this.$t("new_report.time_range_duration_too_small"),
            trigger: "blur"
          }
        ]
      }
    };
  },
  watch: {
    $route() {
      // this watcher is necessary to handle creation
      // intention while the EditReport component is active already
      this.initialize();
    },
    isSuperAdmin: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadTenants();
        }
      }
    }
  },
  computed: {
    ...authStateMappers.mapGetters({user: "getUser", isSuperAdmin: "isSuperAdmin"}),
    ...mspStateMappers.mapGetters({
      customer: "getCustomer",
      categoriesUnfiltered: "getCategories"
    }),
    ...applicationStateMappers.mapGetters({isMobil: 'isMobil'}),
    categories() {
      if (this.report.id) {
        let filteredCategories = this.categoriesUnfiltered
          .filter(category => category.level !== WarningOptionLevelEnum.CREATE)
          .filter(category => category.type === this.report.warningOption.type);

        if (
          filteredCategories.some(option => option.value === this.warningOptionCache.value)) {
          // current selected option is already available in filteredCategories
          return filteredCategories;
        } else {
          // do not forget the current selected option and put it on the first place
          return [this.warningOptionCache].concat(filteredCategories);
        }
      } else {
        return this.categoriesUnfiltered.filter(
          category => category.level === WarningOptionLevelEnum.CREATE
        );
      }
    },
    allCustomers() {
      // For super admin with tenant selected, show tenant customers
      if (this.isSuperAdmin && this.selectedTenantId && this.tenantCustomers.length > 0) {
        return this.tenantCustomers.filter(
          c => !this.report.customers.find(c2 => c.id === c2.id)
        );
      }

      // Default behavior for normal admins/reporters
      return this.customer.filter(
        c => !this.report.customers.find(c2 => c.id === c2.id)
      );
    },
    isMaintenanceReport() {
      return (
        this.report.warningOption &&
        this.report.warningOption.type === WarningOptionTypeEnum.MAINTENANCE
      );
    },
    isMaintenanceTimePeriodActive() {
      return this.isMaintenanceReport &&
        this.report.maintenanceTimeRange.length > 0 && moment(this.report.maintenanceTimeRange[0]).isBefore(moment());
    }
  },
  mounted() {
    // Load tenants for super admin
    if (this.isSuperAdmin) {
      this.loadTenants();
    }

    this.initialize();
  },
  methods: {
    ...mspStateMappers.mapActions([
      "getCustomersAction",
      "getCategoriesAction",
      "createReportAction",
      "patchReportAction",
      "deleteReportAction",
      "getReportAction"
    ]),
    initialize() {
      const promises = [];
      // load report if url id is available, otherwise create a new report
      if (this.$route.params.reportId) {
        promises.push(this.getReportAction(this.$route.params.reportId));
      } else {
        // placeholder promise to respect resolved promise array later
        const tmpReport = new Report();
        tmpReport.author = this.user;
        promises.push(Promise.resolve(tmpReport));
      }
      promises.push(this.getCustomersAction());
      Promise.all(promises).then(data => {
        // data[0] -> the report
        // data[1] -> the customers
        this.report = data[0];

        // check if maintenance could be extended
        if (this.isMaintenanceTimePeriodActive) {
          this.maintenanceExtendedEndDate = this.report.maintenanceTimeRange[1];
          this.maintenanceCreation = false;
        } else {
          this.maintenanceExtendedEndDate = undefined;
          this.maintenanceCreation = true;
        }

        // if this is a new report, use default customer only
        if (!this.$route.params.reportId) {
          this.report.customers = data[1].filter(customer => customer.default);
        }
        if (this.report.warningOption) {
          this.warningOptionCache = {...this.report.warningOption};
        }
      });

      if (this.isSuperAdmin && this.selectedTenantId) {
        this.getCategoriesAction(this.selectedTenantId);
      } else {
        this.getCategoriesAction();
      }
    },
    loadTenants() {
      this.loadingTenants = true;

      // Import tenantService to get all tenants
      import('@/services/TenantService.class').then(({tenantService}) => {
        tenantService.get()
          .then(response => {
            this.tenants = response.data;
          })
          .catch(error => {
            console.error('Error loading tenants:', error);
            this.$message.error(this.$t('report.tenant_load_error'));
          })
          .finally(() => {
            this.loadingTenants = false;
          });
      });
    },
    onTenantChange() {
      if (this.selectedTenantId) {
        this.loadTenantCustomers();

        this.getCategoriesAction(this.selectedTenantId);
      } else {
        // Clear customers if no tenant selected
        this.report.customers = [];
        this.tenantCustomers = [];

        this.getCategoriesAction();
      }
    },
    loadTenantCustomers() {
      this.loadingCustomers = true;

      // Import tenant service to get customers for selected tenant
      import('@/services/TenantService.class').then(({tenantService}) => {
        tenantService.getCustomersByTenant(this.selectedTenantId)
          .then(response => {
            this.tenantCustomers = response.data;

            // Clear current customer selection
            this.report.customers = [];
          })
          .catch(error => {
            console.error('Error loading tenant customers:', error);
            this.$message.error(this.$t('report.customer_load_error'));
          })
          .finally(() => {
            this.loadingCustomers = false;
          });
      });
    },
    onWarningOptionChange() {
      // clear maintenance times when the selected option is not a maintenance option
      if (!this.isMaintenanceReport) {
        this.report.maintenanceTimeRange = undefined;
      }
    },
    removeCustomer(customer) {
      this.report.customers.splice(this.report.customers.indexOf(customer), 1);
    },
    handleCustomerDropdownClick(customer) {
      this.allCustomers.splice(this.allCustomers.indexOf(customer), 1);
      this.report.customers.push(customer);
    },
    cancelClick() {
      this.$router.push({name: "current"});
    },
    deleteReport() {
      this.deleteReportDialog = false;
      this.saveInProgress = true;
      this.deleteReportAction(this.report);
    },
    patchReport() {
      this.$refs.reportForm.validate(valid => {
        if (valid) {
          // show dialog if report is about to be finished
          if (this.report.warningOption.level === WarningOptionLevelEnum.FINISH) {
            this.openReportSavingDialog(
              this.$t("current_reports.report_done_dialog_help"),
              this.$t("current_reports.report_done_dialog_title")
            ).then(action => {
              this.executePatch(action.value)
            }).catch(() => {
            });
            // show dialog if maintenance time range was extended
          } else if (this.maintenanceTimeRangeWasExtended) {
            this.openReportSavingDialog(
              this.$t("current_reports.report_extend_time_range_dialog_help"),
              this.$t("current_reports.report_extend_time_range_dialog_title"),
              this.$t("current_reports.report_extend_time_range_default_reason")
            ).then(action => {
              this.executePatch(action.value)
            }).catch(() => {
            });
            // perfom simple update
          } else {
            this.executePatch();
          }
        }
      });
    },
    openReportSavingDialog(title, help, inputValue) {
      return this.$prompt(
        title,
        help,
        {
          customClass: "report-dialog",
          inputType: "textarea",
          inputValue: inputValue,
          showCancelButton: true,
          confirmButtonText: this.$t("new_report.send_report"),
          confirmButtonClass: "el-button--success",
          cancelButtonText: this.$t("base.cancel"),
          beforeClose: (action, instance, done) => {
            if (action === "confirm") {
              done();
            } else {
              done();
            }
          }
        }
      );
    },
    onMaintenanceExtendedEndDateChange() {
      this.report.maintenanceTimeRange = [this.report.maintenanceTimeRange[0], this.maintenanceExtendedEndDate];
      this.maintenanceTimeRangeWasExtended = true;
    },
    executePatch(finalMessage) {
      this.saveInProgress = true;
      this.patchReportAction({
        report: this.report,
        finalMessage
      }).then(() => {
        this.saveInProgress = false;
        this.$notify.success({
          title: this.$t("notification.report_patched_successfull"),
          message: this.$t("notification.report_patched_successfull_text")
        });
        this.$router.push({name: "current"});
      });
    },
    saveReport() {
      this.$refs.reportForm.validate(valid => {
        if (valid) {
          this.saveInProgress = true;
          this.createReportAction(this.report).then(() => {
            this.saveInProgress = false;
            this.$notify.success({
              title: this.$t("notification.report_created_successfull"),
              message: this.$t("notification.report_created_successfull_text")
            });
            this.$router.push({name: "current"});
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.new-report-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;

  .report-card {
    display: flex;
    width: 100%;
    flex-direction: column;

    &:first-child {
      margin-bottom: 1rem;
    }

    > div {
      padding: 1rem 1rem 0 1rem;
      border-bottom: 1px solid #ebeef5;

      &.bottom {
        padding: 1rem;
        display: flex;
        align-items: flex-end;

        .edition-information-style {
          margin-left: auto;
        }
      }
    }

    .header-item {
      &.customer-input-wrap {
        justify-content: flex-start;
        align-items: flex-start;
        padding-bottom: 1rem;

        .customer-tag {
          margin: 0 0.5em;
        }

        .el-form-item {
          width: 100%;
          margin-bottom: 10px;
        }
      }

      &.report-type-wrap {
        margin-bottom: 0;

        .new-report-select-type {
          width: 100%;
        }
      }

      &.report-subject {
      }
    }

    .content {
      padding: 1rem;
    }

    .datepicker-separator {
      margin: 0 1rem 0 0;
    }

    .date-range-icon {
      margin: 0 0 0 1rem;
    }
  }
}
</style>

<style lang="scss">
.new-report-wrap {
  .report-card {
    input,
    textarea,
    .el-date-editor {
      border: none;
    }

    .report-body-input {
      textarea {
        height: 100%;
      }
    }

    .bottom {
      padding: 1rem;
    }
  }

  .restriction-cover {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(211, 211, 211, 0.3);
    border-radius: 2px;
    margin: -0.25rem;
    cursor: pointer;
    z-index: 10;

    &:not(.no-interaction):hover {
      background-color: rgba(211, 211, 211, 0.4);
    }

    &.no-interaction {
      cursor: initial;
    }
  }

  .readonly-datepicker {
    width: 180px !important;

    input[readonly] {
      cursor: not-allowed;
      color: #acb0b7;
    }
  }
}

.report-dialog {
  max-width: 300px;
}

.edition-mobil {
  flex-flow: column;
  align-items: flex-start;

  button {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>
